/**
 * Aspect — Room geometry pipeline (single entry point)
 * --------------------------------------------------------------
 * ONE module owns every way room geometry is (re)built, replacing the previous
 * overlapping call paths into room_builder / topology_patch / opening_patch /
 * floor_mesh. The contract:
 *
 *   - fullRebuild(app, room, shared)              whole room from scratch
 *   - rebuildWallSubset(app, room, root, ids)     re-render listed walls (+openings)
 *   - removeWalls(root, wallIds, openingIds)      destroy wall/window meshes
 *   - refreshEnvelope(app, room, root)            floor/ceiling/grass resize + floor shape
 *   - clearStructure(app, room, root)             cleared-apartment floor-only shell
 *   - stripStructure(root)                        strip meshes without touching room data
 *
 * Surface materials and floor-shape resolution are shared: every operation uses
 * surfaceOptionsForRoom() so wall tiling / sky reflections can no longer drift
 * between the full-build and patch paths.
 *
 * Every in-place operation fires "scene:geometry-changed" { scope } on the app —
 * systems that track built geometry (wall cutaway opacity/materials) subscribe
 * to that ONE event instead of being manually re-called from every edit site.
 * fullRebuild does NOT fire it: callers assign the new root first and announce
 * "scene:room-loaded" themselves (subscribers hook that for full loads).
 */

import { buildRoom } from "./room_builder.js";
import {
  clearWallMeshes,
  rebuildWallsForIds,
  stripStructureMeshes,
} from "./opening_patch.js";
import { applyFloorOnlyShell, patchRoomEnvelope } from "./topology_patch.js";

/** Uniform surface-library options derived from room data (one resolution path). */
export function surfaceOptionsForRoom(room, shared = {}) {
  const b = room?.bounds;
  const width = room?.dimensions?.width ?? (b ? b.max[0] - b.min[0] : 6);
  const depth = room?.dimensions?.depth ?? (b ? b.max[2] - b.min[2] : 6);
  const height = room?.dimensions?.height ?? 2.8;
  return { width, depth, height, skyTexture: shared.skyTexture ?? null, finishes: room?.finishes ?? {} };
}

function announce(app, scope) {
  app?.fire?.("scene:geometry-changed", { scope });
}

/**
 * Build the whole room from scratch. Returns the new room root entity; the
 * caller owns attaching state and firing "scene:room-loaded".
 */
export function fullRebuild(app, room, shared = {}) {
  return buildRoom(app, room, shared);
}

/**
 * Re-render plaster/frames/glass for the listed wall ids (openings included).
 * Used for opening patches AND wall reshape — an opening patch IS a wall-subset
 * rebuild.
 */
export function rebuildWallSubset(app, room, roomRoot, wallIds, shared = {}) {
  if (!roomRoot || !room || !wallIds?.length) return;
  rebuildWallsForIds(app, room, roomRoot, wallIds, {
    ...shared,
    ...surfaceOptionsForRoom(room, shared),
  });
  announce(app, "walls");
}

/** Destroy wall segments + window panes for removed walls. */
export function removeWalls(app, roomRoot, wallIds, openingIds = []) {
  if (!roomRoot || !wallIds?.length) return;
  clearWallMeshes(roomRoot, wallIds, openingIds);
  announce(app, "walls");
}

/** Resize floor/ceiling/grass slabs and re-resolve the floor shape (polygon/box). */
export function refreshEnvelope(app, room, roomRoot) {
  if (!roomRoot || !room) return;
  patchRoomEnvelope(room, roomRoot, app);
  announce(app, "envelope");
}

/**
 * Cleared-apartment shell: clear room structure data, strip all structure
 * meshes, and rebuild the floor slab (polygon-aware). Floor stays visible.
 */
export function clearStructure(app, room, roomRoot) {
  if (!roomRoot) return;
  applyFloorOnlyShell(room, roomRoot, app);
  announce(app, "structure");
}

/** Strip structure meshes only (room data managed by the caller). */
export function stripStructure(app, roomRoot) {
  if (!roomRoot) return;
  stripStructureMeshes(roomRoot);
  announce(app, "structure");
}
